/** Layout helpers for live cart drawer (full width, theme line-item image sizing). */

const LAYOUT_CSS_KEYS = new Set([
  "display",
  "flex",
  "flexDirection",
  "flexWrap",
  "flexFlow",
  "justifyContent",
  "alignItems",
  "alignContent",
  "gap",
  "rowGap",
  "columnGap",
  "width",
  "maxWidth",
  "minWidth",
  "height",
  "maxHeight",
  "minHeight",
  "margin",
  "marginLeft",
  "marginRight",
  "marginTop",
  "marginBottom",
  "left",
  "right",
  "top",
  "bottom",
  "position",
  "transform",
]);

/** Keep colors/fonts/background from step customCSS; drop flex/canvas layout that shrinks the row. */
export function pickCartDrawerStepVisualCss(stepViewCss = {}) {
  if (!stepViewCss || typeof stepViewCss !== "object") {
    return {};
  }
  const visual = {};
  Object.entries(stepViewCss).forEach(([key, value]) => {
    if (!LAYOUT_CSS_KEYS.has(key)) {
      visual[key] = value;
    }
  });
  return visual;
}

const CART_LINE_IMAGE_SELECTORS = [
  ".cart-item:not(.weupsell-cart-drawer-row) .cart-item__image",
  ".cart-item:not(.weupsell-cart-drawer-row) .cart-item__media",
  ".cart-item:not(.weupsell-cart-drawer-row) .cart-item__media-container",
  ".cart-item:not(.weupsell-cart-drawer-row) td.cart-item__media",
  ".cart-item:not(.weupsell-cart-drawer-row) td:first-child",
  ".cart-item:not(.weupsell-cart-drawer-row) img",
];

function queryInCartDrawer(selector) {
  const roots = [
    document.querySelector("cart-drawer"),
    document.getElementById("CartDrawer"),
    document.querySelector("#cart-drawer"),
    document.querySelector(".cart-drawer"),
  ].filter(Boolean);

  for (const root of roots) {
    const el = root.querySelector(selector);
    if (el && !el.closest(".weupsell-cart-drawer-row")) {
      return el;
    }
  }

  const fallback = document.querySelector(selector);
  if (fallback && !fallback.closest(".weupsell-cart-drawer-row")) {
    return fallback;
  }
  return null;
}

/**
 * Match upsell thumbnail to native cart line item image box (Dawn-like themes).
 * @returns {{ width: number, height: number } | null}
 */
export function measureThemeCartLineItemImageSize() {
  if (typeof document === "undefined") {
    return null;
  }

  for (const selector of CART_LINE_IMAGE_SELECTORS) {
    const el = queryInCartDrawer(selector);
    if (!el) continue;

    const rect = el.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);

    if (width >= 40 && height >= 40) {
      return { width, height };
    }

    if (el.tagName === "IMG" && el.naturalWidth > 0) {
      const w = width || el.naturalWidth;
      const h = height || el.naturalHeight;
      if (w >= 40 && h >= 40) {
        return { width: w, height: h };
      }
    }
  }

  return null;
}

export const CART_DRAWER_DEFAULT_LINE_IMAGE = { width: 80, height: 80 };

const FLOW_SECTION_GAP_PX = 8;

function parseDimensionValue(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const match = String(value).trim().match(/^([\d.]+)\s*(%|px)?$/i);
  if (!match) return Number(value) || 0;
  return Number(match[1]) || 0;
}

/** Live cart drawer: stack blocks top-to-bottom in the same order as the editor canvas (by Y). */
export function sortSectionsForCartDrawerFlow(sections, isMobile) {
  const view = isMobile ? "mobile" : "desktop";
  return [...(sections || [])].sort((a, b) => {
    const ay = Number(a?.setting?.position?.displacement?.[view]?.y) || 0;
    const by = Number(b?.setting?.position?.displacement?.[view]?.y) || 0;
    if (ay !== by) return ay - by;
    const ax = Number(a?.setting?.position?.displacement?.[view]?.x) || 0;
    const bx = Number(b?.setting?.position?.displacement?.[view]?.x) || 0;
    return ax - bx;
  });
}

export function getCartDrawerFlowStackBounds(sections, isMobile) {
  const view = isMobile ? "mobile" : "desktop";
  let width = 0;
  let height = 0;
  const list = sections || [];

  list.forEach((section, index) => {
    const dimension = section?.setting?.dimension?.[view] || {};
    const w = parseDimensionValue(dimension.width);
    const h = parseDimensionValue(dimension.height);
    width = Math.max(width, w);
    height += h + (index > 0 ? FLOW_SECTION_GAP_PX : 0);
  });

  return {
    width: Math.max(width, 280),
    height: Math.max(height, 120),
  };
}
